import type { Experience, ShapeProps, ShapeState } from '@kouto/types'

type NonNullableObject<T extends object> = { [K in keyof T]: NonNullable<T[K]> }

export type MinimalShapeEntity = Pick<
  Experience,
  'id' | 'shape' | 'position' | 'number' | 'maximumAllowedToBook'
> & {
  unavailable?: boolean
  title?: string
  hoverText?: string
}

interface CustomOption {
  focusedResources?: Set<string>
  selectedResource?: string
  draggable?: boolean
}

const DESKTOP_PIN_SIZE = 36
const MOBILE_PIN_SIZE = 24

const formatResourceAsShape = ({
  resource,
  size,
  isSelected = false,
  isFocused = false,
  draggable = false,
}: {
  resource: NonNullableObject<MinimalShapeEntity>
  size: number
  isSelected?: boolean
  isFocused?: boolean
  draggable?: boolean
}): ShapeProps => ({
  id: resource.id,
  type: resource.shape.type,
  x: resource.position?.x || 0,
  y: resource.position?.y || 0,
  name: resource.title,
  width: size,
  height: size,
  color: resource.shape.color,
  disabled: resource.unavailable ?? false,
  tooltip: resource.unavailable ? 'Unavailable' : undefined,
  hoverText: resource.hoverText,
  opacity: 1,
  label:
    resource.number[0] === '#' ? resource.number.slice(1) : resource.number,
  draggable,
  isSelected,
  isFocused,
  maximumAllowedToBook: resource.maximumAllowedToBook,
})

function formatResourceGroupToShapes<
  T extends { experiences: MinimalShapeEntity[] },
>(
  resourceGroup: T,
  containerSize: { width: number; height: number },
  imageSize: { width: number; height: number },
  { focusedResources, selectedResource, draggable }: CustomOption = {},
): ShapeState {
  const shapes = {} as ShapeState
  const isMobile = containerSize.width < 400 || containerSize.height < 400
  let pinSize = isMobile ? MOBILE_PIN_SIZE : DESKTOP_PIN_SIZE
  const containerRatio = containerSize.width / containerSize.height
  const imageRatio = imageSize.width / imageSize.height

  const mapVsContainerRatio = containerRatio / imageRatio
  if (mapVsContainerRatio) {
    if (containerRatio < imageRatio) {
      pinSize = pinSize / mapVsContainerRatio
    } else {
      pinSize = pinSize * mapVsContainerRatio
    }
  }

  Object.values(resourceGroup.experiences).forEach(experience => {
    if (experience.shape && experience.number) {
      shapes[experience.id] = formatResourceAsShape({
        resource: experience as NonNullableObject<MinimalShapeEntity>,
        size: pinSize,
        isFocused: focusedResources?.has(experience.id) || false,
        isSelected: selectedResource === experience.id,
        draggable,
      })
    }
  })

  return shapes
}

export function getShapesFromResourceGroups<
  T extends { experiences: MinimalShapeEntity[] },
>(
  resourceGroups: T[],
  containerSize: { width: number; height: number },
  imageSize: { width: number; height: number },
  customOption: CustomOption = {},
): ShapeState {
  const shapes = {} as ShapeState

  resourceGroups.forEach(group => {
    Object.assign(
      shapes,
      formatResourceGroupToShapes(
        group,
        containerSize,
        imageSize,
        customOption,
      ),
    )
  })

  return shapes
}
